import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getDocs,
  increment,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebase/config";
import {
  CreateEngagementCampaignDTO,
  EngagementCampaign,
  UpdateEngagementCampaignDTO,
} from "../types/engagement";

const CAMPAIGNS_COLLECTION = "engagementCampaigns";
const DISPATCH_ENGAGEMENT_CAMPAIGN_ENDPOINT =
  process.env.REACT_APP_DISPATCH_ENGAGEMENT_CAMPAIGN_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/dispatchEngagementCampaign";

class EngagementService {
  private async postAuthenticatedJson<T>(
    url: string,
    body: Record<string, unknown>,
  ): Promise<T> {
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("Authentication required. Please sign in.");
    }
    const idToken = await currentUser.getIdToken();
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });
    const rawText = await response.text();
    let responseData: T | { error?: string; message?: string } | null = null;
    try {
      responseData = rawText ? JSON.parse(rawText) : null;
    } catch {
      responseData = null;
    }
    if (!response.ok) {
      const message =
        (responseData as any)?.error ||
        (responseData as any)?.message ||
        rawText ||
        `Request failed with status ${response.status}`;
      throw new Error(message);
    }
    return responseData as T;
  }

  private toDate(value: unknown): Date {
    if (value instanceof Timestamp) return value.toDate();
    if (value instanceof Date) return value;
    if ((value as any)?.toDate && typeof (value as any).toDate === "function") {
      return (value as any).toDate();
    }
    const parsed = new Date(String(value || ""));
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  private mapCampaign(data: any): EngagementCampaign {
    return {
      campaignId: String(data.campaignId || ""),
      userId: String(data.userId || ""),
      appId: String(data.appId || ""),
      appName: String(data.appName || ""),
      firebaseProjectId: String(data.firebaseProjectId || ""),
      name: String(data.name || ""),
      objective: String(data.objective || "").trim() || undefined,
      status: data.status || "draft",
      message: {
        title: String(data.message?.title || ""),
        body: String(data.message?.body || ""),
        imageUrl: String(data.message?.imageUrl || "").trim() || undefined,
        deepLink: String(data.message?.deepLink || "").trim() || undefined,
      },
      localizedMessages:
        data.localizedMessages && typeof data.localizedMessages === "object"
          ? Object.fromEntries(
              Object.entries(data.localizedMessages)
                .map(([locale, value]) => {
                  const safeLocale = String(locale || "").trim();
                  if (!safeLocale || !value || typeof value !== "object") return null;
                  return [
                    safeLocale,
                    {
                      title: String((value as any).title || "").trim(),
                      body: String((value as any).body || "").trim(),
                    },
                  ] as const;
                })
                .filter((entry): entry is readonly [string, { title: string; body: string }] =>
                  Boolean(entry),
                ),
            )
          : undefined,
      targeting: {
        mode: data.targeting?.mode || "all_users",
        languageCode:
          String(data.targeting?.languageCode || "").trim() || undefined,
        languageTopic:
          String(data.targeting?.languageTopic || "").trim() || undefined,
        allUsersTopic: String(data.targeting?.allUsersTopic || ""),
        condition: String(data.targeting?.condition || ""),
        localeTargets: Array.isArray(data.targeting?.localeTargets)
          ? data.targeting.localeTargets
              .map((item: any) => ({
                languageCode: String(item?.languageCode || "").trim(),
                languageName: String(item?.languageName || "").trim(),
                topic: String(item?.topic || "").trim(),
                condition: String(item?.condition || "").trim(),
              }))
              .filter((item: any) => item.languageCode && item.topic)
          : undefined,
      },
      schedule: data.schedule
        ? {
            type: (["send_now", "one_time", "recurring"].includes(String(data.schedule?.type))
              ? String(data.schedule?.type)
              : "send_now") as "send_now" | "one_time" | "recurring",
            timezone: String(data.schedule?.timezone || "").trim() || "UTC",
            startAt: data.schedule?.startAt
              ? this.toDate(data.schedule.startAt)
              : new Date(),
            recurrence:
              data.schedule?.recurrence && typeof data.schedule.recurrence === "object"
                ? {
                    frequency: (["daily", "weekly", "monthly"].includes(
                      String(data.schedule.recurrence.frequency),
                    )
                      ? String(data.schedule.recurrence.frequency)
                      : "daily") as "daily" | "weekly" | "monthly",
                    interval: Math.max(1, Number(data.schedule.recurrence.interval || 1)),
                    weekdays: Array.isArray(data.schedule.recurrence.weekdays)
                      ? data.schedule.recurrence.weekdays
                          .map((day: unknown) => String(day || "").trim().toUpperCase())
                          .filter(Boolean)
                      : undefined,
                    dayOfMonth: data.schedule.recurrence.dayOfMonth
                      ? Number(data.schedule.recurrence.dayOfMonth)
                      : undefined,
                  }
                : undefined,
            endAt: data.schedule?.endAt ? this.toDate(data.schedule.endAt) : undefined,
          }
        : undefined,
      nextRunAt: data.nextRunAt ? this.toDate(data.nextRunAt) : undefined,
      runCount: Number(data.runCount || 0),
      scheduleVersion: Number(data.scheduleVersion || 0),
      dispatchLock:
        data.dispatchLock && typeof data.dispatchLock === "object"
          ? {
              token: String(data.dispatchLock.token || ""),
              expiresAt: this.toDate(data.dispatchLock.expiresAt),
            }
          : undefined,
      lastRunSummary:
        data.lastRunSummary && typeof data.lastRunSummary === "object"
          ? {
              attempted: Number(data.lastRunSummary.attempted || 0),
              sent: Number(data.lastRunSummary.sent || 0),
              failed: Number(data.lastRunSummary.failed || 0),
              failedTargets: Array.isArray(data.lastRunSummary.failedTargets)
                ? data.lastRunSummary.failedTargets.map((item: unknown) => String(item || "")).filter(Boolean)
                : [],
            }
          : undefined,
      retryState:
        data.retryState && typeof data.retryState === "object"
          ? {
              failedTargets: Array.isArray(data.retryState.failedTargets)
                ? data.retryState.failedTargets.map((item: unknown) => String(item || "")).filter(Boolean)
                : [],
              nextRetryAt: data.retryState.nextRetryAt
                ? this.toDate(data.retryState.nextRetryAt)
                : undefined,
              retryCount: Number(data.retryState.retryCount || 0),
            }
          : undefined,
      analytics: {
        sent: Number(data.analytics?.sent || 0),
        delivered: Number(data.analytics?.delivered || 0),
        opened: Number(data.analytics?.opened || 0),
        clicked: Number(data.analytics?.clicked || 0),
        ctr: Number(data.analytics?.ctr || 0),
      },
      createdAt: this.toDate(data.createdAt),
      updatedAt: this.toDate(data.updatedAt),
      lastSentAt: data.lastSentAt ? this.toDate(data.lastSentAt) : undefined,
    };
  }

  private generateCampaignId() {
    return `engage_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  async listUserCampaigns(userId: string): Promise<EngagementCampaign[]> {
    const q = query(
      collection(db, CAMPAIGNS_COLLECTION),
      where("userId", "==", userId),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((item) => this.mapCampaign(item.data()))
      .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
  }

  async createCampaign(
    userId: string,
    dto: CreateEngagementCampaignDTO,
  ): Promise<EngagementCampaign> {
    const campaignId = this.generateCampaignId();
    const payload = {
      campaignId,
      userId,
      appId: dto.appId,
      appName: dto.appName,
      firebaseProjectId: dto.firebaseProjectId,
      name: dto.name,
      objective: dto.objective || "",
      status: dto.status || "draft",
      message: dto.message,
      localizedMessages: dto.localizedMessages || null,
      targeting: dto.targeting,
      schedule: dto.schedule || null,
      nextRunAt:
        dto.status === "scheduled" && dto.schedule?.startAt
          ? dto.schedule.startAt
          : null,
      runCount: 0,
      scheduleVersion: 1,
      dispatchLock: null,
      lastRunSummary: null,
      retryState: null,
      analytics: {
        sent: 0,
        delivered: 0,
        opened: 0,
        clicked: 0,
        ctr: 0,
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...(dto.status === "sent" ? { lastSentAt: serverTimestamp() } : {}),
    };
    await setDoc(doc(db, CAMPAIGNS_COLLECTION, campaignId), payload);
    return {
      ...this.mapCampaign(payload),
      createdAt: new Date(),
      updatedAt: new Date(),
      lastSentAt: dto.status === "sent" ? new Date() : undefined,
    };
  }

  async updateCampaign(
    campaignId: string,
    updates: UpdateEngagementCampaignDTO,
  ): Promise<void> {
    const docRef = doc(db, CAMPAIGNS_COLLECTION, campaignId);
    const payload: Record<string, any> = {
      updatedAt: serverTimestamp(),
    };
    if (updates.appId !== undefined) payload.appId = updates.appId;
    if (updates.appName !== undefined) payload.appName = updates.appName;
    if (updates.firebaseProjectId !== undefined) {
      payload.firebaseProjectId = updates.firebaseProjectId;
    }
    if (updates.name !== undefined) payload.name = updates.name;
    if (updates.objective !== undefined) payload.objective = updates.objective;
    if (updates.message !== undefined) payload.message = updates.message;
    if (updates.localizedMessages !== undefined) {
      payload.localizedMessages = updates.localizedMessages || null;
    }
    if (updates.targeting !== undefined) payload.targeting = updates.targeting;
    if (updates.schedule !== undefined) payload.schedule = updates.schedule || null;
    if (updates.schedule !== undefined) {
      payload.scheduleVersion = increment(1);
    }
    if (updates.status !== undefined) payload.status = updates.status;
    if (updates.status === "sent") payload.lastSentAt = serverTimestamp();

    await updateDoc(docRef, payload);
  }

  async deleteCampaign(campaignId: string): Promise<void> {
    await deleteDoc(doc(db, CAMPAIGNS_COLLECTION, campaignId));
  }

  async dispatchCampaign(
    campaignId: string,
    options?: { dryRun?: boolean },
  ): Promise<{
    success: boolean;
    campaignId: string;
    mode: string;
    dryRun: boolean;
    sentCount: number;
    failures: Array<{ target: string; error: string }>;
  }> {
    return this.postAuthenticatedJson(DISPATCH_ENGAGEMENT_CAMPAIGN_ENDPOINT, {
      campaignId,
      dryRun: Boolean(options?.dryRun),
    });
  }
}

const engagementService = new EngagementService();
export default engagementService;
